/**
 * The network: everybody who was accepted, and what they have made since.
 *
 * The review queue answers "is this take any good". This screen answers the
 * question that comes after it — who is actually carrying the work, who has
 * gone quiet, and what the whole thing is costing. So the tiles describe the
 * network rather than the page, and every row leads to one person's profile
 * rather than trying to say everything at once.
 *
 * Read-only by design. Suspending somebody, reviewing their audio and paying
 * them already have their own screens; a roster that could also do all three
 * would be a worse version of each.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { BackOfficeNav } from "../components/BackOfficeNav";
import { Meter, MeterRow } from "../components/Meter";
import { Pagination } from "../components/Pagination";
import { RefreshButton } from "../components/RefreshButton";
import { ReviewSignIn } from "../components/ReviewSignIn";
import { useRefreshOnFocus } from "../hooks/useRefreshOnFocus";
import { isCredentialFailure } from "../lib/backOffice";
import { clearReviewCredentials, readReviewCredentials } from "../lib/session";
import { approvalLabel, lastSeen, ratesDiffer } from "../lib/roster";
import { formatDate } from "../lib/validation";
import { rosterAdmin, type ReviewCreds } from "../api/endpoints";
import type { Roster, RosterFilter, RosterRow, RosterSort } from "../types/api";

const FILTERS: Array<{ id: RosterFilter | "all"; label: string }> = [
  { id: "all", label: "Everyone" },
  { id: "active", label: "Active" },
  { id: "onboarding", label: "Still onboarding" },
  { id: "suspended", label: "Suspended" },
];

const SORTS: Array<{ id: RosterSort; label: string }> = [
  { id: "joined", label: "Newest first" },
  { id: "approved", label: "Most approved" },
  { id: "earned", label: "Earned the most" },
  { id: "owed", label: "Owed the most" },
  { id: "seen", label: "Last signed in" },
  { id: "name", label: "Name" },
];

export function AdminContributors() {
  const [creds, setCreds] = useState<ReviewCreds | null>(() => readReviewCredentials());
  const [data, setData] = useState<Roster | null>(null);
  const [filter, setFilter] = useState<RosterFilter | "all">("all");
  const [sort, setSort] = useState<RosterSort>("joined");
  const [country, setCountry] = useState<string>("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  // Every control that narrows the list also resets the page: page 3 of a
  // different question is not a page anybody asked for.
  useEffect(() => {
    if (!creds) return;
    let cancelled = false;
    const timer = window.setTimeout(
      () => {
        void (async () => {
          try {
            setLoadError(null);
            const result = await rosterAdmin.list(creds, {
              status: filter === "all" ? undefined : filter,
              country: country || undefined,
              search: search.trim() || undefined,
              sort,
              page,
            });
            if (!cancelled) setData(result);
          } catch (err) {
            if (cancelled) return;
            // A rejected key means sign in again; anything else is transient
            // and must not throw somebody out of the screen.
            if (isCredentialFailure(err)) {
              clearReviewCredentials();
              setCreds(null);
            } else {
              setLoadError((err as Error).message);
            }
          } finally {
            if (!cancelled) setLoading(false);
          }
        })();
      },
      // Debounced while typing in the search box, immediate otherwise.
      search ? 350 : 0,
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [creds, filter, sort, country, search, page, nonce]);

  useRefreshOnFocus(reload);

  if (!creds) return <ReviewSignIn onSubmit={setCreds} />;

  const summary = data?.summary;
  const showRates = data ? ratesDiffer(data.contributors) : false;

  return (
    <Layout showNav={false} showAccount={false} title="Contributors">
      <section className="section">
        <div className="wrap">
          <BackOfficeNav />

          <div className="row-between">
            <div className="eyebrow">Contributors</div>
            <RefreshButton onClick={reload} busy={loading} />
          </div>
          <h1 className="display" style={{ fontSize: "clamp(22px,4vw,32px)", margin: "0 0 18px" }}>
            The network
          </h1>

          {/* Whole network, whatever the filter says. A number that moved
              when somebody typed in the search box would answer nothing.
              Three rows of three, in the order they get asked about: the
              people, then the work, then the money. */}
          <MeterRow>
            <Meter label="Contributors" value={summary?.contributors.toLocaleString()} />
            <Meter
              label="Recorded this week"
              value={summary?.recorded_last_7_days.toLocaleString()}
            />
            <Meter label="Still onboarding" value={summary?.onboarding.toLocaleString()} />
          </MeterRow>
          {/* Responses, counted. No money in this row: a dollar figure
              beside three counts reads as a total of them. */}
          <MeterRow>
            <Meter label="Approved responses" value={summary?.approved.toLocaleString()} />
            <Meter label="In review" value={summary?.pending.toLocaleString()} />
            <Meter label="On air" value={summary?.published.toLocaleString()} />
          </MeterRow>
          {/* The three places the money is, in the order it moves through
              them. Lifetime earned is not a fourth tile because it is not a
              fourth pot — it is these last two added up. */}
          <MeterRow>
            <Meter
              label="Owed if approved"
              value={summary && `$${summary.pending_value.formatted}`}
            />
            <Meter
              label="Owed now"
              value={summary && `$${summary.balance_outstanding.formatted}`}
              tone={summary && summary.balance_outstanding.cents > 0 ? "warn" : undefined}
            />
            <Meter label="Paid out" value={summary && `$${summary.paid_out.formatted}`} />
          </MeterRow>

          {summary && summary.countries.length > 1 && (
            <CountryStrip
              countries={summary.countries}
              selected={country}
              onSelect={(code) => {
                setCountry(code);
                setPage(1);
              }}
            />
          )}

          <div className="filter-row mt-24">
            {FILTERS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`chip ${filter === entry.id ? "chip-active" : ""}`}
                onClick={() => {
                  setFilter(entry.id);
                  setPage(1);
                }}
              >
                {entry.label}
                {entry.id === "suspended" && summary?.suspended
                  ? ` (${summary.suspended})`
                  : ""}
              </button>
            ))}
          </div>

          <div className="roster-controls mt-16">
            <div className="field" style={{ margin: 0 }}>
              <input
                value={search}
                placeholder="Search by name, email, handle or id…"
                aria-label="Search contributors"
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label htmlFor="roster-sort" className="sr-label">
                Sort by
              </label>
              <select
                id="roster-sort"
                value={sort}
                onChange={(event) => {
                  setSort(event.target.value as RosterSort);
                  setPage(1);
                }}
              >
                {SORTS.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading && !data ? (
            <div className="stack mt-16" aria-busy="true" aria-label="Loading contributors">
              {[0, 1, 2].map((row) => (
                <div key={row} className="panel panel-tight log-skeleton">
                  <div className="skeleton-line skeleton-title" />
                  <div className="skeleton-line skeleton-meta" />
                </div>
              ))}
            </div>
          ) : loadError ? (
            <div className="panel empty mt-16">
              <h3>Couldn't load the roster</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>{loadError}</p>
              <button type="button" className="btn btn-ghost mt-16" onClick={reload}>
                Try again
              </button>
            </div>
          ) : data && data.contributors.length === 0 ? (
            <div className="panel empty mt-16">
              <h3>{search ? "Nobody matches that" : "Nobody here yet"}</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                {search
                  ? "Try part of a name, an email address or a handle."
                  : filter === "suspended"
                    ? "No suspended accounts — which is the good outcome."
                    : "Accepted applicants show up here once they finish signing up."}
              </p>
            </div>
          ) : (
            <>
              <div className="log-count muted mt-16">
                {data?.total.toLocaleString()}{" "}
                {data?.total === 1 ? "contributor" : "contributors"}
                {search || filter !== "all" || country ? " matching" : ""}
              </div>
              <div className="stack">
                {data?.contributors.map((row) => (
                  <ContributorRow key={row.contributor_id} row={row} showRate={showRates} />
                ))}
              </div>
              {data && (
                <Pagination
                  page={data.page}
                  hasMore={data.has_more}
                  total={data.total}
                  noun={data.total === 1 ? "contributor" : "contributors"}
                  busy={loading}
                  onChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}

function CountryStrip({
  countries,
  selected,
  onSelect,
}: {
  countries: Roster["summary"]["countries"];
  selected: string;
  onSelect: (code: string) => void;
}) {
  return (
    <div className="roster-countries mt-24">
      <div className="card-label" style={{ margin: 0 }}>
        By country
      </div>
      <div className="filter-row" style={{ marginTop: 10 }}>
        {countries.map((entry) => (
          <button
            key={entry.country}
            type="button"
            className={`chip ${selected === entry.country ? "chip-active" : ""}`}
            // Clicking the country you are already on clears it, so the
            // strip is both a breakdown and a filter without a second row of
            // controls.
            onClick={() => onSelect(selected === entry.country ? "" : entry.country)}
            // "??" is the bucket for anybody without one, which the API
            // cannot filter on.
            disabled={entry.country === "??"}
          >
            {entry.country_name} · {entry.contributors}
          </button>
        ))}
      </div>
    </div>
  );
}

function ContributorRow({ row, showRate }: { row: RosterRow; showRate: boolean }) {
  const { metrics } = row;
  const suspended = row.status === "suspended";

  return (
    <Link
      to={`/admin/contributors/${encodeURIComponent(row.contributor_id)}`}
      className="panel panel-tight roster-row"
    >
      <div className="row-between" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div className="roster-name">
            {row.name}
            {row.handle && <span className="roster-handle mono">@{row.handle}</span>}
          </div>
          <div className="log-meta muted">
            {row.email}
            {row.country_name ? ` · ${row.country_name}` : ""}
            {showRate ? ` · $${row.rate.formatted} a response` : ""}
            {row.joined_at ? ` · joined ${formatDate(row.joined_at)}` : ""}
          </div>
          <div className="log-meta muted">{lastSeen(row, formatDate)}</div>
        </div>
        <div className="log-pills">
          {suspended && <span className="pill pill-rejected">Suspended</span>}
          {!row.onboarded && <span className="pill pill-pending">Onboarding</span>}
          {metrics.recorded_last_7_days > 0 && (
            <span className="pill pill-approved">
              {metrics.recorded_last_7_days} this week
            </span>
          )}
        </div>
      </div>

      <div className="roster-metrics">
        <div>
          <span className="k">Approved</span>
          <span className="v">{metrics.approved.toLocaleString()}</span>
        </div>
        <div>
          <span className="k">In review</span>
          <span className="v">{metrics.pending.toLocaleString()}</span>
        </div>
        <div>
          <span className="k">Approval rate</span>
          <span className="v">{approvalLabel(metrics.approval_rate)}</span>
        </div>
        <div>
          <span className="k">Earned</span>
          <span className="v">${metrics.lifetime_earned.formatted}</span>
        </div>
        <div>
          <span className="k">Owed</span>
          <span className="v">${metrics.balance.formatted}</span>
        </div>
      </div>
    </Link>
  );
}
